import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ConfigService } from '../../shared-services/config.service';
import { Subject, takeUntil } from 'rxjs';

type ToggleGroup = {
  title: string;
  key?: string;
  options: string[];
};

type ToggleGroupResponse = {
  groups: ToggleGroup[];
};

@Component({
  selector: 'multiselect-toggle',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multiselect-toggle.html',
  styleUrl: './multiselect-toggle.css'
})
export class MultiselectToggle implements OnInit, OnChanges, OnDestroy {
  @Input() data?: { dataUrl?: string; groups?: ToggleGroup[]; isQueryParam?: boolean };
  @Input() optionsMap: Record<string, string[]> = {};
  @Input() dataUrl = '/assets/multiselect-toggle-data/multiselect-toggle.json';
  @Input() groups: ToggleGroup[] = [
    {
      title: 'Region',
      options: ['AME', 'CHINA', 'CIS', 'EUROPE', 'INDIA', 'Latin America', 'NESEA', 'North America', 'South Pacific'],
    },
    {
      title: 'Segment',
      options: ['Defense', 'Marine', 'Mining', 'Oil & Gas', 'PowerGen', 'Rail'],
    },
  ];

  private initialGroups: ToggleGroup[] = [];
  private unSubscribe$ = new Subject<void>();
  private selectedByGroup = new Map<string, Set<string>>();
  private readonly regionExpandMap: Record<string, string[]> = {
    AME: ['AFRICA', 'MIDDLE EAST'],
    CIS: ['CIS - AME', 'CIS - Europe'],
  };

  constructor(
    private http: HttpClient,
    private globalFilterService: GlobalFilterService,
    private configService: ConfigService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['optionsMap']) {
      this.applyDynamicOptions();
    }
  }

  ngOnInit(): void {
    this.loadToggleData();
    this.subscribeToFilters();
  }

  private loadToggleData(): void {
    const url = this.data?.dataUrl || this.dataUrl;
    if (!url) return;

    // Use ConfigService to append current filters if it's not a local asset
    const filters = this.globalFilterService.getCurrentFilters();
    const isLocal = url.startsWith('assets/') || url.startsWith('/assets/');

    if (isLocal) {
      this.http.get<ToggleGroupResponse>(url).subscribe({
        next: (response) => this.handleToggleResponse(response),
      });
    } else {
      this.configService.getConfig(url, this.data, filters, 'get').subscribe({
        next: (response) => this.handleToggleResponse(response),
      });
    }
  }

  private handleToggleResponse(response: any): void {
    const groups = response?.groups || response?.data?.groups || [];
    if (Array.isArray(groups) && groups.length > 0) {
      this.groups = groups;
      this.normalizeSubRegionTitle();
      this.initialGroups = JSON.parse(JSON.stringify(this.groups));
      this.applyDynamicOptions();
      this.syncSelectionFromFilters(this.globalFilterService.getCurrentFilters());
    }
  }

  private subscribeToFilters(): void {
    this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        this.syncSelectionFromFilters(filters);

        if (this.shouldClearSelectionState(filters)) {
          this.selectedByGroup.clear();
        }

        // Re-load data if the update came from a chart (legend sync or widget interaction)
        // or if it's an explicit refresh request.
        if (filters?.legendSync || (filters?.widgetId && !filters?.skipReload)) {
          this.loadToggleData();
        }
      });
  }

  toggleOption(groupTitle: string, option: string): void {
    const selected = this.selectedByGroup.get(groupTitle) ?? new Set<string>();
    if (selected.has(option)) {
      selected.delete(option);
    } else {
      selected.add(option);
    }
    this.selectedByGroup.set(groupTitle, selected);
    const group = this.groups.find((item) => item.title === groupTitle);
    const filterKey = this.resolveFilterKey(group);
    if (!filterKey) {
      return;
    }

    const selectedValues = Array.from(selected);
    this.globalFilterService.activeFilter = filterKey;

    if (selectedValues.length === 0) {
      // Clear complementary alias keys before emitting the reset.
      // The region chart writes its filter under 'region' (legend click) while the top
      // toggle writes under 'sub_region'. Both must be cleared so charts see a clean state.
      if (filterKey === 'sub_region') {
        this.globalFilterService.deleteKey('region', this.data?.isQueryParam === true);
      } else if (filterKey === 'region') {
        this.globalFilterService.deleteKey('sub_region', this.data?.isQueryParam === true);
      }
      this.globalFilterService.resetFilterSelection(filterKey, this.data?.isQueryParam === true);
      return;
    }

    // Uppercase only specific region labels for the payload, keep UI labels unchanged
    const TARGET_UPPER_KEYS = ['latin america', 'north america', 'south pacific'];
    let payloadValues = selectedValues.map(v => {
      if (typeof v !== 'string') return v;
      const norm = v.trim().toLowerCase();
      return TARGET_UPPER_KEYS.includes(norm) ? v.toUpperCase() : v;
    });


    if (filterKey === 'sub_region') {
      // Expand + dedupe
      const expanded = payloadValues.flatMap(v => {
        if (typeof v !== 'string') return [String(v)];
        return this.regionExpandMap[v] ?? [v];
      });

      payloadValues = Array.from(new Set(expanded));
    }

    this.globalFilterService.updateFilterSelection(
      filterKey,
      payloadValues,
      this.data?.isQueryParam === true,
    );
  }

  isSelected(groupTitle: string, option: string): boolean {
    const localSelected = this.selectedByGroup.get(groupTitle);
    if (localSelected?.has(option)) {
      return true;
    }

    return this.isSelectedFromCurrentFilters(groupTitle, option);
  }

  private applyDynamicOptions(): void {
    if (!this.optionsMap || Object.keys(this.optionsMap).length === 0) {
      return;
    }

    this.groups = this.groups.map((group) => {
      const dynamicOptions = this.optionsMap[this.resolveOptionsMapKey(group)];

      if (Array.isArray(dynamicOptions) && dynamicOptions.length > 0) {
        const initialGroup = this.initialGroups.find(ig => this.resolveOptionsMapKey(ig) === this.resolveOptionsMapKey(group));

        const processedOptions = dynamicOptions
          .filter(opt => {
            const norm = this.normalizeValue(opt);
            // Don't add 'INTERNATIONAL' as a dynamic button if it's not in the initial config
            if (norm === 'international') {
              return !!initialGroup?.options.some(orig => this.normalizeValue(orig) === 'international');
            }
            return true;
          })
          .map(opt => {
            if (initialGroup) {
              // Find a match in the original options, ignoring case
              const originalMatch = initialGroup.options.find(
                orig => orig.toLowerCase() === opt.toLowerCase()
              );
              return originalMatch || opt;
            }
            return opt;
          });

        const selectedValues = this.getSelectedValuesForGroup(group);
        const normalizedProcessed = new Set(
          processedOptions.map((opt) => this.normalizeValue(opt)),
        );

        selectedValues.forEach((selectedValue) => {
          const normalizedSelected = this.normalizeValue(selectedValue);
          if (normalizedProcessed.has(normalizedSelected)) {
            return;
          }

          // Don't add dynamic buttons from filter sync if they weren't in the initial config.
          // This prevents legend clicks (e.g. INDUSTRIAL, INTERNATIONAL) from injecting
          // extra buttons into the top toggle filters.
          const isInInitial = initialGroup?.options.some(orig => this.normalizeValue(orig) === normalizedSelected);
          if (!isInInitial) return;

          const fromInitial = initialGroup?.options.find(
            (opt) => this.normalizeValue(opt) === normalizedSelected,
          );
          const fromCurrent = group.options.find(
            (opt) => this.normalizeValue(opt) === normalizedSelected,
          );

          processedOptions.push(fromInitial || fromCurrent || selectedValue);
          normalizedProcessed.add(normalizedSelected);
        });

        return {
          ...group,
          options: processedOptions,
        };
      }

      return group;
    });

    this.remapSelectionsToCurrentOptions();
  }

  private resolveOptionsMapKey(group?: ToggleGroup): string {
    const filterKey = this.resolveFilterKey(group);
    return filterKey === 'sub_region' ? 'region' : filterKey;
  }

  private normalizeSubRegionTitle(): void {
    this.groups = this.groups.map(g => {
      const normalizedTitle = (g.title || '').trim().toLowerCase();
      if (normalizedTitle === 'sub region' || normalizedTitle === 'sub_region') {
        return { ...g, title: 'Region', key: 'sub_region' }; // UI shows Region, payload stays sub_region
      }
      return g;
    });
  }

  private resolveFilterKey(group?: ToggleGroup): string {
    if (!group) {
      return '';
    }

    if (group.key) {
      return group.key;
    }

    return group.title.toLowerCase().replace(/\s+/g, '_');
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private syncSelectionFromFilters(filters: any): void {
    if (!filters) return;

    const queryParams = filters.queryParams || {};
    const requestParams = filters.requestParams || {};

    this.groups.forEach((group) => {
      const filterKeys = this.getFilterKeyCandidates(group);
      const rawFilterValue = this.getFirstFilterValue(filterKeys, requestParams, queryParams);

      if (rawFilterValue === undefined || rawFilterValue === null) {
        this.selectedByGroup.delete(group.title);
        return;
      }

      const values = this.normalizeIncomingValues(group, this.toStringArray(rawFilterValue));
      if (!values.length) {
        this.selectedByGroup.delete(group.title);
        return;
      }

      const optionsByNormalized = new Map<string, string>(
        group.options.map((opt) => [this.normalizeValue(opt), opt]),
      );

      const selectedUiValues = values
        .map((value) => optionsByNormalized.get(this.normalizeValue(value)))
        .filter((value): value is string => !!value);

      if (selectedUiValues.length > 0) {
        this.selectedByGroup.set(group.title, new Set(selectedUiValues));
      }
    });
  }

  private normalizeIncomingValues(group: ToggleGroup, rawValues: string[]): string[] {
    const isSubRegionGroup = this.getFilterKeyCandidates(group).includes('sub_region');
    if (!isSubRegionGroup) {
      return rawValues;
    }

    const normalizedSet = new Set(rawValues.map((value) => this.normalizeValue(value)));
    const collapsedValues: string[] = [];

    const ameExpanded = this.regionExpandMap['AME'].map((value) => this.normalizeValue(value));
    const cisExpanded = this.regionExpandMap['CIS'].map((value) => this.normalizeValue(value));

    const hasAmeExpanded = ameExpanded.every((value) => normalizedSet.has(value));
    if (hasAmeExpanded) {
      collapsedValues.push('AME');
      ameExpanded.forEach((value) => normalizedSet.delete(value));
    }

    const hasCisExpanded = cisExpanded.every((value) => normalizedSet.has(value));
    if (hasCisExpanded) {
      collapsedValues.push('CIS');
      cisExpanded.forEach((value) => normalizedSet.delete(value));
    }

    collapsedValues.push(...Array.from(normalizedSet));
    return collapsedValues;
  }

  private remapSelectionsToCurrentOptions(): void {
    this.groups.forEach((group) => {
      const selected = this.selectedByGroup.get(group.title);
      if (!selected || selected.size === 0) {
        return;
      }

      const optionsByNormalized = new Map<string, string>(
        group.options.map((opt) => [this.normalizeValue(opt), opt]),
      );

      const remapped = Array.from(selected)
        .map((value) => optionsByNormalized.get(this.normalizeValue(value)))
        .filter((value): value is string => !!value);

      this.selectedByGroup.set(group.title, new Set(remapped));
    });
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => String(item));
    }
    return [String(value)];
  }

  private normalizeValue(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private isSelectedFromCurrentFilters(groupTitle: string, option: string): boolean {
    const group = this.groups.find(
      (item) => this.normalizeValue(item.title) === this.normalizeValue(groupTitle),
    );
    if (!group) {
      return false;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    if (!currentFilters) {
      return false;
    }

    const queryParams = currentFilters.queryParams || {};
    const requestParams = currentFilters.requestParams || {};
    const filterKeys = this.getFilterKeyCandidates(group);
    const rawFilterValue = this.getFirstFilterValue(filterKeys, requestParams, queryParams);

    if (rawFilterValue === undefined || rawFilterValue === null) {
      return false;
    }

    const values = this.normalizeIncomingValues(group, this.toStringArray(rawFilterValue));
    const normalizedOption = this.normalizeValue(option);

    return values.some((value) => this.normalizeValue(value) === normalizedOption);
  }

  private shouldClearSelectionState(filters: any): boolean {
    if (!filters || filters.legendSync === true) {
      return false;
    }

    const isExplicitResetEvent =
      (filters.changedFilterKey === null || filters.changedFilterKey === undefined) &&
      (filters.widgetId === null || filters.widgetId === undefined) &&
      !filters.filterListener &&
      !filters.skipReload;
    if (!isExplicitResetEvent) {
      return false;
    }

    const hasQueryParams = Object.prototype.hasOwnProperty.call(filters, 'queryParams');
    const hasRequestParams = Object.prototype.hasOwnProperty.call(filters, 'requestParams');
    if (!hasQueryParams || !hasRequestParams) {
      return false;
    }

    return (
      Object.keys(filters.queryParams || {}).length === 0 &&
      Object.keys(filters.requestParams || {}).length === 0
    );
  }

  private getFilterKeyCandidates(group: ToggleGroup): string[] {
    const resolved = this.resolveFilterKey(group);
    const optionsMapKey = this.resolveOptionsMapKey(group);
    const keys = new Set<string>([resolved, optionsMapKey].filter(Boolean));

    if (resolved === 'sub_region' || optionsMapKey === 'sub_region') {
      keys.add('region');
    }
    if (resolved === 'region' || optionsMapKey === 'region') {
      keys.add('sub_region');
    }

    return Array.from(keys);
  }

  private getFirstFilterValue(keys: string[], requestParams: any, queryParams: any): any {
    for (const key of keys) {
      if (requestParams?.[key] !== undefined && requestParams?.[key] !== null) {
        return requestParams[key];
      }
      if (queryParams?.[key] !== undefined && queryParams?.[key] !== null) {
        return queryParams[key];
      }
    }
    return undefined;
  }

  private getSelectedValuesForGroup(group: ToggleGroup): string[] {
    const selected = new Set<string>();
    const localSelected = this.selectedByGroup.get(group.title);
    localSelected?.forEach((value) => selected.add(value));

    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    if (!currentFilters) {
      return Array.from(selected);
    }

    const queryParams = currentFilters.queryParams || {};
    const requestParams = currentFilters.requestParams || {};
    const filterKeys = this.getFilterKeyCandidates(group);
    const rawFilterValue = this.getFirstFilterValue(filterKeys, requestParams, queryParams);

    if (rawFilterValue !== undefined && rawFilterValue !== null) {
      const values = this.normalizeIncomingValues(group, this.toStringArray(rawFilterValue));
      values.forEach((value) => selected.add(value));
    }

    return Array.from(selected);
  }

}
